import { action, computed, observable, makeAutoObservable } from 'mobx';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { FormInputState, FormModel, group, Result, ResultValue } from 'src_common/common/mobx-utils/Form';
import { SpecialSportsModelType } from 'src/api/config/special_sports/getSpecialSports';
import { InputStatus } from 'src/components/Input/InputStatus';
import {
    validateExpectLink,
    validateExpectNumber,
    validateIsNumber,
    validateRequire,
} from 'src/api/utils/formValidators';
import { apiCommon } from 'src/api/ApiCommon';
import { Response200Type } from 'src/api_openapi/generated/openapi_website_cms_getTopNavigationLinks';
import { FilesState } from '../FilesState/FilesState';
import { UploadImageState } from '../UploadImageState/UploadImageState';
import { sportsOptions } from 'src/components/NavigationLinkPanel/NavigationLinkForm/sportOption';

export type LandingPageContentResponse =
    | HeaderNavigationLinkLoading
    | PromotionLandingPageReady
    | HeaderNavigationLinkError;

interface HeaderNavigationLinkLoading {
    type: 'loading';
}

type HeaderNavigationLinkType = any | null;
interface PromotionLandingPageReady {
    type: 'ready';
    value: Array<HeaderNavigationLinkType>;
}

interface OptionType {
    name: string;
    key: string;
}

interface HeaderNavigationLinkError {
    type: 'error';
    value: string;
}
export interface PromotionLandingPageTypeSubmitType {
    LP: NavigationLinkType;
    id?: string;
}
export interface NavigationLinkType {
    title: string;
    event_id: string;
    img_url: string;
    date_from: string;
    date_to: string;
    is_active: boolean;
    universe: string;
    sport: string;
    competition_id: number | null | undefined;
    cta_url: string | null | undefined;
}

export interface NavigationLinkGetType {
    title: string;
    event_id: string;
    img_url: string;
    date_from: string;
    date_to: string;
    is_active: boolean;
    universe: string;
    visibility: string;
    id: number;
    sport: string;
    action_template: string;
    competition_id: number | null;
    display_order: number | null | undefined;
    cta_url: string | null | undefined;
}

export class TopNavigationLinkItem {
    constructor(
        public title: string,
        public event_id: string,
        public img_url: string,
        public date_from: string,
        public date_to: string,
        public is_active: boolean,
        public universe: string,
        public sport: string,
        public cta_url: string | null | undefined,
        public competition_id: number | null | undefined
    ) {}
}

type PageType = 'edit' | 'add';

export class TopNavigationLinkState {
    @observable readonly navigationLinkResource: Resource<Response200Type | null>;
    @observable private readonly specialSportsListResource: Resource<Array<SpecialSportsModelType>>;
    @observable readonly universe: string | null;
    @observable page: string = 'add';
    @observable editedLink: NavigationLinkGetType | null = null;
    @observable imgErrorMessage: string | null = null;
    @observable isLoading: boolean = false;
    @observable errorMessage: string = '';
    @observable imgWarningMessage: string | null = null;
    @observable isActiveFlagChanged: boolean = false;
    @observable sizeError: boolean = false;
    orderOption: FormInputState<string> = new FormInputState('', false);
    directionOption: FormInputState<string> = new FormInputState('event-id', false);
    visibilityOption: FormInputState<string> = new FormInputState('both', false);

    readonly linkNameModel: FormModel<string>;
    readonly linkName: FormInputState<string>;
    readonly linkNameStatus: InputStatus;
    readonly eventIdModel: FormModel<string>;
    readonly eventId: FormInputState<string>;
    readonly eventIdStatus: InputStatus;
    readonly dateFromModel: FormModel<string>;
    readonly dateFrom: FormInputState<string>;
    readonly dateFromStatus: InputStatus;
    readonly dateToModel: FormModel<string>;
    readonly dateTo: FormInputState<string>;
    readonly dateToStatus: InputStatus;
    readonly imgUrlModel: FormModel<string>;
    readonly imgUrl: FormInputState<string>;
    readonly isActiveModel: FormModel<boolean>;
    readonly isActive: FormInputState<boolean>;
    readonly isActiveStatus: InputStatus;
    readonly ctaUrlModel: FormModel<string>;
    readonly ctaUrl: FormInputState<string>;
    readonly ctaUrlStatus: InputStatus;
    readonly competitionIdModel: FormModel<number | null>;
    readonly competitionId: FormInputState<string>;
    readonly competitionIdStatus: InputStatus;
    readonly sportModel: FormModel<string>;
    readonly sport: FormInputState<string>;
    readonly sportStatus: InputStatus;
    readonly form: FormModel<NavigationLinkType>;
    readonly filesState: FilesState;

    public uploadIconImg: UploadImageState;

    constructor(universe: string | null) {
        this.uploadIconImg = new UploadImageState('navigation-link', 'svg+xml');
        this.universe = universe;
        this.filesState = new FilesState('top-navigation-link');
        this.navigationLinkResource = new Resource(async (): Promise<Response200Type | null> => {
            this.isLoading = true;
            const response = await apiCommon.getHeaderNavigationLinks.run({});
            this.isLoading = false;
            return response;
        });

        this.specialSportsListResource = new Resource(async (): Promise<Array<SpecialSportsModelType>> => {
            return await apiCommon.getSpecialSports.run({});
        });

        this.linkName = new FormInputState('', false);
        this.linkNameModel = this.linkName.map(validateRequire);
        this.linkNameStatus = new InputStatus(this.linkNameModel);

        this.eventId = new FormInputState('', false);
        this.eventIdModel = this.eventId.map(validateIsNumber);
        this.eventIdStatus = new InputStatus(this.eventIdModel);

        this.dateFrom = new FormInputState('', false);
        this.dateFromModel = this.dateFrom.map(validateRequire);
        this.dateFromStatus = new InputStatus(this.dateFromModel);

        this.dateTo = new FormInputState('', false);
        this.dateToModel = this.dateTo.map(validateRequire);
        this.dateToStatus = new InputStatus(this.dateToModel);

        this.isActive = new FormInputState(false, false);
        this.isActiveModel = this.isActive.toModel();
        this.isActiveStatus = new InputStatus(this.isActiveModel);

        this.imgUrl = new FormInputState('', false);
        this.imgUrlModel = this.imgUrl.toModel();

        this.imgUrl = new FormInputState('', false);
        this.imgUrlModel = this.imgUrl.toModel();

        this.ctaUrl = new FormInputState('', false);
        this.ctaUrlModel = this.ctaUrl.map(validateExpectLink);
        this.ctaUrlStatus = new InputStatus(this.ctaUrlModel);

        this.competitionId = new FormInputState('', false);
        this.competitionIdModel = this.competitionId.map(validateExpectNumber);
        this.competitionIdStatus = new InputStatus(this.competitionIdModel);

        this.sport = new FormInputState('', false);
        this.sportModel = this.sport.toModel();
        this.sportStatus = new InputStatus(this.sportModel);

        this.form = group({
            title: this.linkNameModel,
            img_url: this.imgUrlModel,
            event_id: this.eventIdModel,
            date_from: this.dateFromModel,
            date_to: this.dateToModel,
            is_active: this.isActiveModel,
            universe: this.linkNameModel,
            sport: this.sportModel,
            competition_id: this.competitionIdModel,
            cta_url: this.ctaUrlModel,
        }).map((item: NavigationLinkType): Result<NavigationLinkType> => {
            return new ResultValue({
                title: item.title,
                event_id: item.event_id,
                date_from: item.date_from,
                date_to: item.date_to,
                img_url: item.img_url,
                is_active: item.is_active,
                universe: item.universe,
                sport: item.sport,
                competition_id: item.competition_id,
                cta_url: item.cta_url,
            });
        });
        makeAutoObservable(this);
    }
    @computed get navigationLinks(): LandingPageContentResponse {
        const navLinks = this.navigationLinkResource.get();
        if (navLinks.type === 'ready' && navLinks.value !== null) {
            return {
                type: 'ready',
                value: navLinks.value,
            };
        }
        if (navLinks.type === 'loading') {
            return {
                type: 'loading',
            };
        }

        return {
            type: 'error',
            value: 'Something went wrong with fetching landing page info',
        };
    }

    @computed public get specialSportsListResponse(): Array<SpecialSportsModelType> | null {
        const specialSports = this.specialSportsListResource.get();
        if (specialSports.type === 'ready' && specialSports.value !== null) {
            return specialSports.value.filter((sport) => sport.is_active && sport.sport_type === 'new-sport');
        }
        return null;
    }

    @computed public get specialSportsOptions(): Array<OptionType> {
        return this.specialSportsListResponse === null
            ? []
            : this.specialSportsListResponse.map((specialSport) => {
                  return { name: specialSport.label, key: specialSport.slug };
              });
    }

    @computed public get allSportsOptions(): Array<OptionType> {
        return [...sportsOptions, ...this.specialSportsOptions].sort((a, b) => a.name.localeCompare(b.name));
    }

    @computed get isEditMode(): boolean {
        return this.page === 'edit';
    }

    @action checkSizeOfUploadedImage = (url: string | undefined) => {
        if (url !== undefined) {
            const img = new Image();

            img.src = url;
            if (img !== null && img.width !== 16 && img.height !== 16) {
                this.setSizeError(true);
            } else if (this.sizeError === true) {
                this.setSizeError(false);
            }
        }
    };

    @action handleCancel = (): void => {
        if (this.isEditMode && this.editedLink !== null) {
            this.setEditedLink(this.editedLink);
        } else {
            this.uploadIconImg.handleChooseImage('');
            this.form.reset();
        }
    };

    @computed public get competitionIsNotFull(): boolean {
        return this.competitionId.isVisited === true && this.competitionId.value === '' && this.sport.value === '';
    }

    @computed public get nextOffInfoIsNotFull(): boolean {
        return (
            this.competitionId.isVisited === true &&
            this.eventId.isVisited === true &&
            this.competitionId.value === '' &&
            this.eventId.value === ''
        );
    }

    @computed public get nextOffSubmitCheck(): boolean {
        return (
            this.directionOption.value === 'next-off-race' &&
            this.competitionId.value !== '' &&
            this.eventId.value !== '' &&
            this.sport.value !== ''
        );
    }
    @computed public get competitionSubmitCheck(): boolean {
        return (
            this.directionOption.value === 'competition-id' &&
            this.competitionId.value !== '' &&
            this.sport.value !== ''
        );
    }
    @computed public get eventIdSubmitCheck(): boolean {
        return this.directionOption.value === 'event-id' && this.eventId.value !== '';
    }
    @computed public get directLinkSubmitCheck(): boolean {
        return this.directionOption.value === 'direct-link' && this.ctaUrl.value !== '';
    }
    @computed public get sportSubmitCheck(): boolean {
        return this.directionOption.value === 'sport' && this.sport.value !== '';
    }

    @computed public get submitAllowed(): boolean {
        return (
            this.nextOffSubmitCheck ||
            this.competitionSubmitCheck ||
            this.eventIdSubmitCheck ||
            this.directLinkSubmitCheck ||
            this.sportSubmitCheck
        );
    }

    @action setSizeError = (sizeError: boolean): void => {
        this.sizeError = sizeError;
    };

    @action setEditedLink = (editedLink: NavigationLinkGetType) => {
        this.editedLink = editedLink;
        this.uploadIconImg.handleChooseImage(editedLink.img_url ?? '');
        if (editedLink !== null && editedLink !== undefined) {
            this.linkName.setValue(editedLink.title);
            this.eventId.setValue(editedLink.event_id);
            this.dateFrom.setValue(editedLink.date_from);
            this.dateTo.setValue(editedLink.date_to);
            this.isActive.setValue(editedLink.is_active);
            this.ctaUrl.setValue(editedLink.cta_url ?? '');
            this.competitionId.setValue(editedLink.competition_id?.toString() ?? '');
            this.sport.setValue(editedLink.sport ?? '');
            this.orderOption.setValue(editedLink.display_order?.toString() ?? '');
            this.directionOption.setValue(editedLink.action_template ?? 'event-id');
            this.visibilityOption.setValue(editedLink.visibility ?? 'both');
        }
    };

    @computed get isFormValid(): boolean {
        return this.form.result instanceof ResultValue;
    }

    @computed get navigationLinksSorted(): Array<NavigationLinkGetType> | null {
        if (this.navigationLinks.type === 'ready') {
            return this.navigationLinks.value.sort((lp1: NavigationLinkGetType, lp2: NavigationLinkGetType) =>
                lp1 !== null && lp2 !== null ? lp1.title.localeCompare(lp2.title) : -1
            );
        }
        return [];
    }

    @action setDefaultState = (): void => {
        this.setPage('add');
        this.editedLink = null;
        this.uploadIconImg.handleChooseImage('');
        this.form.reset();
    };

    @action resetForm = (): void => {
        this.form.reset();
    };

    @action onRemove = async (element: NavigationLinkGetType) => {
        const removeResult = await apiCommon.deleteHeaderNavigationLink.run({ id: element.id });

        if (removeResult) {
            await this.navigationLinkResource.refresh();

            this.form.reset();
        } else {
            this.errorMessage = 'Unknown error. Something went wrong.';
        }
    };

    @action setActiveFlag = async (element: NavigationLinkGetType) => {
        this.isActiveFlagChanged = true;
        this.isLoading = true;

        const payload: NavigationLinkGetType = {
            ...element,
            is_active: !element.is_active,
        };

        const submitResult = await this.submitEditedNavigationLink(payload, element.id);

        if (submitResult) {
            await this.navigationLinkResource.refresh();
        } else {
            this.errorMessage = 'Unknown error. Something went wrong.';
        }
        this.isActiveFlagChanged = false;
        this.isLoading = false;
    };

    @action submitNavigationLink = async (navigationLink: NavigationLinkType): Promise<number | Error | null> => {
        let response: number | Error | null;
        try {
            const navigationLinkExtended = {
                ...navigationLink,
                display_order: Number(this.orderOption.value),
                action_template: this.directionOption.value,
                visibility: this.visibilityOption.value,
            };
            const navigationLinkObject = {
                navigationLink: navigationLinkExtended,
            };
            response = await apiCommon.postHeaderNavigationLink.run(navigationLinkObject);
        } catch (error) {
            console.error(error);
            return null;
        }
        return response;
    };

    @action submitEditedNavigationLink = async (
        navigationLink: NavigationLinkType,
        id: number
    ): Promise<number | Error | null> => {
        let response: number | Error | null;
        try {
            const navigationLinkExtended = {
                ...navigationLink,
                display_order: Number(this.orderOption.value),
                action_template: this.directionOption.value,
                visibility: this.visibilityOption.value,
            };
            const navigationLinkObject = {
                navigationLink: navigationLinkExtended,
                id: id,
            };
            response = await apiCommon.putHeaderNavigationLink.run(navigationLinkObject);
        } catch (error) {
            console.error(error);
            return null;
        }
        return response;
    };

    @action handleSubmit = async () => {
        this.isLoading = true;
        this.errorMessage = '';
        this.form.setAsVisited();
        const result = this.form.result;

        if (result instanceof ResultValue) {
            const payload: TopNavigationLinkItem = {
                title: result.value.title,
                img_url: this.uploadIconImg.imgUrl,
                date_from: result.value.date_from,
                date_to: result.value.date_to,
                is_active: result.value.is_active,
                event_id: result.value.event_id,
                sport: result.value.sport,
                competition_id: result.value.competition_id,
                cta_url: result.value.cta_url,
                universe: this.universe !== null ? this.universe : 'star',
            };
            const submitResult = this.editedLink
                ? await this.submitEditedNavigationLink(payload, this.editedLink.id)
                : await this.submitNavigationLink(payload);

            if (submitResult) {
                await this.navigationLinkResource.refresh();
                this.setDefaultState();
            } else {
                this.errorMessage = 'Unknown error. Something went wrong.';
            }
        }

        this.isLoading = false;
    };

    @action setPage = (value: PageType): void => {
        this.page = value;
    };

    static createForContext(): TopNavigationLinkState {
        return new TopNavigationLinkState('star');
    }
}
